import dayjs from "dayjs";

import { CalendarMonthRounded } from "@mui/icons-material";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Box, CircularProgress, IconButton, Tooltip, Typography } from "@mui/material";
import { DataGridPro, GridColDef } from "@mui/x-data-grid-pro";

import { ApiRoutesItem } from "@/api/routes";
import { ListViewType } from "@/constants";
import { AIRPORT_MAP } from "@/constants/airport";
import { NEED_BACKGROUND_PROGRAM, airlineBgColor, maxLastSeenTime } from "@/constants/config";
import { PROGRAM_IMG, PROGRAM_NAME_MAP } from "@/constants/programs";
import usePlatform from "@/hooks/usePlatform";
import useThemeData from "@/hooks/useThemeData";
import { CabinsType } from "@/typings";

import CabinColorItem from "../RoutesItem/CabinColorItem";

interface Props {
  data: ApiRoutesItem[];
  refreshIds: string[];
  onClick: (
    params: ApiRoutesItem & {
      type: CabinsType;
    }
  ) => void;
}

const RoutesListTable = (props: Props) => {
  const { data } = props;
  const { isMobile, isPc } = usePlatform();
  const align = isMobile ? "left" : "center";
  const theme = useThemeData();

  const darkListBackground = theme.isDarkMode ? "#2D2D2D" : "#F5F5F5";

  const columns: GridColDef<ApiRoutesItem>[] = [
    {
      field: "date",
      headerName: "Date",
      flex: 1,
      maxWidth:90,
      align,
      sortComparator: (v1, v2) => {
        return dayjs(v1).unix() - dayjs(v2).unix();
      },
      renderCell: ({ row }) => {
        return (
          <div className="text-center">
            <Typography fontSize={13} variant="body1">
              {dayjs(row.date).format("ddd")}
            </Typography>
            <Box
              className="px-[5px] rounded-[5px]"
              sx={{ backgroundColor: theme.isDarkMode ? airlineBgColor : "#e1f5fe" }}
            >
              <Typography fontSize={13} variant="body1">
                {dayjs(row.date).format("M/D")}
              </Typography>
            </Box>
          </div>
        );
      },
    },
    {
      field: "points",
      headerName: "Last Seen",
      flex: 1,
      align,
      minWidth: 100,
      sortComparator: (a: ApiRoutesItem, b: ApiRoutesItem) => {

        if (a.ls > b.ls) {
          return -1;
        }

        return 1;
      },
      renderCell({ row }) {
        const refreshing = props.refreshIds.includes(row.id);
        return (
          <Typography variant="caption" fontSize={13} className="flex items-center">
            {row.points.ls ? dayjs(row.points.ls * 1000).fromNow() : "few days ago"}
            {Date.now() / 1000 - row.points.ls < maxLastSeenTime && (
              <CheckCircleIcon className="!text-[16px] ml-[3px]" color="primary" />
            )}
            {refreshing && (
              <CircularProgress size={14} color="primary" thickness={6} className="ml-[5px]" />
            )}
          </Typography>
        );
      },
    },
    {
      field: "points_rate",
      headerName: "Program",
      flex: 1,
      align,
      minWidth: 100,
      sortable: false,
      renderCell({ row }) {
        return (
          <Tooltip placement="top" title={PROGRAM_NAME_MAP.get(row.program)}>
            <div className="flex items-center space-x-[10px]">
              <Box
                className={
                  isPc
                    ? "rounded-[3px] p-[1px] flex items-center h-[40px] w-[75px]"
                    : "rounded-[3px] p-[1px] flex items-center h-[40px] w-[50px]"
                }
                sx={{
                  backgroundColor:
                    theme.isDarkMode && NEED_BACKGROUND_PROGRAM.includes(row.program)
                      ? airlineBgColor
                      : "transparent",
                }}
              >
                <img className="w-[100%]" src={PROGRAM_IMG[row.program]} alt="" />
              </Box>
              <Typography
                fontSize={13}
                className="text-center"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {row.program}
              </Typography>
            </div>
          </Tooltip>
        );
      },
    },
    {
      field: "Departs",
      headerName: "Departs",
      flex: 1,
      align,
      sortable: false,
      maxWidth:75,
      renderCell({ row }) {
        const airports = row.route.split("-");
        return (
          <Tooltip
            title={
              AIRPORT_MAP[airports[0]]?.name && AIRPORT_MAP[airports[0]]?.city
                ? `${AIRPORT_MAP[airports[0]].name}, ${AIRPORT_MAP[airports[0]].city}`
                : airports[0]
            }
          >
            <Typography className="inline" fontSize={14}>
              {airports[0]}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: "Arrives",
      headerName: "Arrives",
      flex: 1,
      align,
      maxWidth:75,
      sortable: false,
      renderCell({ row }) {
        const airports = row.route.split("-");
        return (
          <Tooltip
            title={
              AIRPORT_MAP[airports[1]]?.name && AIRPORT_MAP[airports[1]]?.city
                ? `${AIRPORT_MAP[airports[1]].name}, ${AIRPORT_MAP[airports[1]].city}`
                : airports[1]
            }
          >
            <Typography className="inline" fontSize={14}>
              {/* {AIRPORT_MAP[airports[1]]?.city
                ? `${AIRPORT_MAP[airports[1]].city}(${airports[1]})`
                : airports[1]} */}
              {airports[1]}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: "Economy",
      headerName: "Economy",
      flex: 1,
      align,
      minWidth: 150,
      valueGetter(params) {
        return params.row.points.y;
      },
      renderCell({ row }) {
        return (
          <CabinColorItem
            mode={ListViewType.Table}
            data={row}
            type="y"
            onClick={() => {
              props.onClick({
                ...row,
                type: "Economy",
              });
            }}
          />
        );
      },
      sortComparator: (a: number, b: number) => {
        if (a === 0 || a === undefined || a === null) {
          return 1;
        } else if (b === 0 || b === undefined || b === null) {
          return -1;
        }

        if (a < b) {
          return -1;
        }

        return 1;
      },
    },
    {
      field: "Premium",
      headerName: "Premium",
      flex: 1,
      align,
      minWidth: 150,
      valueGetter(params) {
        return params.row.points.w;
      },
      renderCell({ row }) {
        return (
          <CabinColorItem
            mode={ListViewType.Table}
            data={row}
            type="w"
            onClick={() => {
              props.onClick({
                ...row,
                type: "Premium Economy",
              });
            }}
          />
        );
      },
      sortComparator: (a: number, b: number) => {
        if (a === 0 || a === undefined || a === null) {
          return 1;
        } else if (b === 0 || b === undefined || b === null) {
          return -1;
        }

        if (a < b) {
          return -1;
        }

        return 1;
      },
    },
    {
      field: "Business",
      headerName: "Business",
      flex: 1,
      align,
      minWidth: 150,
      valueGetter(params) {
        return params.row.points.j;
      },
      renderCell({ row }) {
        return (
          <CabinColorItem
            mode={ListViewType.Table}
            data={row}
            type="j"
            onClick={() => {
              props.onClick({
                ...row,
                type: "Business",
              });
            }}
          />
        );
      },
      sortComparator: (a: number, b: number) => {
        if (a === 0 || a === undefined || a === null) {
          return 1;
        } else if (b === 0 || b === undefined || b === null) {
          return -1;
        }

        if (a < b) {
          return -1;
        }

        return 1;
      },
    },
    {
      field: "First",
      headerName: "First",
      flex: 1,
      align,
      minWidth: 150,
      valueGetter(params) {
        return params.row.points.f;
      },
      renderCell({ row }) {
        return (
          <CabinColorItem
            mode={ListViewType.Table}
            data={row}
            type="f"
            onClick={() => {
              props.onClick({
                ...row,
                type: "First",
              });
            }}
          />
        );
      },
      sortComparator: (a: number, b: number) => {
        if (a === 0 || a === undefined || a === null) {
          return 1;
        } else if (b === 0 || b === undefined || b === null) {
          return -1;
        }

        if (a < b) {
          return -1;
        }

        return 1;
      },
    },
    {
      field: "action",
      headerName: "",
      flex: 1,
      maxWidth:70,
      align,
      sortable: false,
      renderCell({ row }) {
        return (
          <div className="flex flex-col items-start md:items-end justify-center h-[100%]">
            <Tooltip title="View flight details">
              <IconButton
                size="small"
                onClick={() => {
                  props.onClick({
                    ...row,
                    type: "Economy",
                  });
                }}
              >
                <AutoAwesomeRoundedIcon color="primary" />
              </IconButton>
            </Tooltip>
            {row.supported_route && row.supported_route != 0 ? (
              <Tooltip title="View calendar availability">
                <IconButton
                  size="small"
                  onClick={() => {
                    window.open(`/route-detail?route=${row.route}&program=${row.program}`, "_self");
                  }}
                >
                  <CalendarMonthRounded color="primary" />
                </IconButton>
              </Tooltip>
            ) : null}
          </div>
        );
      },
    },
  ];

  return (
    <div className="overflow-x-auto">
      <div className="min-w-[1200px]">
        <DataGridPro
          disableColumnMenu
          getRowId={(row) => row.id + row.key}
          rows={data}
          columns={columns}
          rowHeight={70}
          rowSelection={false}
          hideFooter
          disableColumnResize
          sx={{
            ".MuiDataGrid-columnHeaderTitleContainer": {
              justifyContent: "center",
            },
            "& .MuiDataGrid-row:nth-of-type(odd)": {
              backgroundColor: darkListBackground,
            },
          }}
        />
      </div>
    </div>
  );
};

export default RoutesListTable;
